/**
 * Pure helper that turns the model schema + visible-sheet list into the
 * 3-section tree the `<TreeNavigator>` renders. No React, no DOM —
 * fully unit-testable.
 *
 * Sections:
 *   1. **Outputs** — single "Outputs" branch holding every output leaf,
 *      sorted alphabetically. The blueprint specifies a richer split via
 *      `displayConfig.performanceOutputs` / `capitalStackOutputs` / "Other"
 *      when the schema carries a `displayConfig` field. The current
 *      `ModelViewSchemaSubset` (`apps/web/src/lib/api.ts`) does NOT project
 *      `displayConfig` — when a future change extends the subset, branch
 *      construction here can split outputs into 2-3 named groups without
 *      changing the section/leaf shape consumers depend on.
 *   2. **Inputs** — branches by `category`; within each category, sorted
 *      `critical → high → medium → low → undefined → alpha-by-label`.
 *   3. **Cash Flows** — top-level leaves only (no branches). Visible sheet
 *      names matching `/cashflow|cf$/i`. Targets `{sheet, row: 1, col: 1}`
 *      so a click navigates to A1 of that sheet.
 *
 * Leaves whose schema entry is missing `sheet` or `cell`, or whose `cell`
 * fails A1 parsing, get `target: null` — the renderer treats that as a
 * disabled non-clickable item. A single `console.warn` summarizes the count
 * per call so the implementer can spot bad data without per-leaf log spam.
 */
import type { ModelViewResponse } from '@/lib/api';
import { parseA1 } from './grid-helpers';

type SchemaSubset = ModelViewResponse['schema'];

export interface TreeLeaf {
  kind: 'leaf';
  /** Stable id — used as React key + `data-testid` suffix. */
  id: string;
  label: string;
  /** Resolved `{sheet, row, col}` target, or null when address can't be parsed. */
  target: { sheet: string; row: number; col: number } | null;
  meta?: { priority?: string; category?: string };
}

export interface TreeBranch {
  kind: 'branch';
  id: string;
  label: string;
  children: TreeLeaf[];
}

export type TreeSectionId = 'outputs' | 'inputs' | 'cashflows';

export interface TreeSection {
  id: TreeSectionId;
  label: string;
  branches: TreeBranch[];
  /** Top-level leaves (used by the Cash Flows section; empty for the others). */
  leaves: TreeLeaf[];
}

interface RawInput {
  name?: string;
  displayName?: string;
  sheet?: string;
  cell?: string;
  category?: string;
  priority?: string;
}

interface RawOutput {
  name?: string;
  displayName?: string;
  sheet?: string;
  cell?: string;
  category?: string;
}

const PRIORITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function priorityOrder(p: string | undefined): number {
  return p && p in PRIORITY_RANK ? PRIORITY_RANK[p] : 4;
}

function resolveTarget(
  sheet: string | undefined,
  cellA1: string | undefined,
): TreeLeaf['target'] {
  if (!sheet || !cellA1) return null;
  const parsed = parseA1(cellA1);
  if (!parsed) return null;
  return { sheet, row: parsed.row, col: parsed.col };
}

const CASHFLOW_SHEET_RE = /cashflow|cf$/i;

export function buildTree(
  schema: SchemaSubset,
  visibleSheetNames: readonly string[],
): TreeSection[] {
  const inputsRaw = (schema.inputs as RawInput[]) ?? [];
  const outputsRaw = (schema.outputs as RawOutput[]) ?? [];
  let missingTargetCount = 0;

  // ---- Outputs ----------------------------------------------------------
  const outputsLeaves: TreeLeaf[] = [];
  for (const o of outputsRaw) {
    if (!o.name) continue;
    const target = resolveTarget(o.sheet, o.cell);
    if (target === null) missingTargetCount += 1;
    outputsLeaves.push({
      kind: 'leaf',
      id: `outputs.${o.name}`,
      label: o.displayName ?? o.name,
      target,
      meta: o.category ? { category: o.category } : undefined,
    });
  }
  outputsLeaves.sort((a, b) => a.label.localeCompare(b.label));

  const outputsBranches: TreeBranch[] =
    outputsLeaves.length > 0
      ? [
          {
            kind: 'branch',
            id: 'outputs.all',
            label: 'Outputs',
            children: outputsLeaves,
          },
        ]
      : [];

  // ---- Inputs -----------------------------------------------------------
  const inputsByCategory = new Map<string, TreeLeaf[]>();
  for (const i of inputsRaw) {
    if (!i.name) continue;
    const target = resolveTarget(i.sheet, i.cell);
    if (target === null) missingTargetCount += 1;
    const leaf: TreeLeaf = {
      kind: 'leaf',
      id: `inputs.${i.name}`,
      label: i.displayName ?? i.name,
      target,
      meta: { priority: i.priority, category: i.category },
    };
    const cat = i.category ?? 'uncategorized';
    if (!inputsByCategory.has(cat)) inputsByCategory.set(cat, []);
    inputsByCategory.get(cat)!.push(leaf);
  }
  for (const leaves of inputsByCategory.values()) {
    leaves.sort((a, b) => {
      const pa = priorityOrder(a.meta?.priority);
      const pb = priorityOrder(b.meta?.priority);
      if (pa !== pb) return pa - pb;
      return a.label.localeCompare(b.label);
    });
  }
  const inputsBranches: TreeBranch[] = [...inputsByCategory.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cat, children]) => ({
      kind: 'branch',
      id: `inputs.${cat}`,
      label: cat,
      children,
    }));

  // ---- Cash Flows -------------------------------------------------------
  const cashflowLeaves: TreeLeaf[] = visibleSheetNames
    .filter((n) => CASHFLOW_SHEET_RE.test(n))
    .map((s) => ({
      kind: 'leaf',
      id: `cashflows.${s}`,
      label: s,
      target: { sheet: s, row: 1, col: 1 },
    }));

  if (missingTargetCount > 0) {
    // Single summary warning so a model with a few derived/hidden outputs
    // doesn't flood the console; the canonical model logs nothing.
    console.warn(
      `[tree-builder] ${missingTargetCount} leaf node(s) missing sheet/cell — rendered as disabled non-clickable items.`,
    );
  }

  return [
    {
      id: 'outputs',
      label: 'Outputs',
      branches: outputsBranches,
      leaves: [],
    },
    {
      id: 'inputs',
      label: 'Inputs',
      branches: inputsBranches,
      leaves: [],
    },
    {
      id: 'cashflows',
      label: 'Cash Flows',
      branches: [],
      leaves: cashflowLeaves,
    },
  ];
}
